import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Spinner } from "@/components/ui/spinner";
import { formatDate, formatTime } from "@/utils/format/date-time";
import { useUpdateAppointment } from "../../api/update-appointment";
import { useAppointment } from "../../api/get-appointments";
import { useAvailableAppointments } from "@/api/get-available-appointments";
import { AppointmentForm } from "../appointment-form/appointment-form";
import { format } from "date-fns";

const schema = yup
    .object({
        date: yup.string().required("Please select a date"),
        time: yup.string().required("Please select a time"),
        confirmContact: yup
            .boolean()
            .oneOf([true], "Please select this option")
            .required(),
        confirmationType: yup
            .array()
            .min(1, "Please select one option")
            .max(1, "Please select one option"),
    })
    .required();

export const EditAppointment = () => {
    const { id } = useParams();

    const { data: appointment, isLoading } = useAppointment(id);

    const [activeMonth, setActiveMonth] = useState(() =>
        format(new Date(), "yyyyMM"),
    );

    // Prevent too many renders
    const [syncedId, setSyncedId] = useState(null);

    if (appointment?.startDate && syncedId !== id) {
        setSyncedId(id);
        setActiveMonth(format(new Date(appointment.startDate), "yyyyMM"));
    }

    const { data: availableDates = [], isLoading: isAvailableDatesLoading } =
        useAvailableAppointments(
            appointment?.clinicianID,
            activeMonth,
            appointment?.serviceGuid,
            true,
            false,
        );

    const startDate = (() => {
        const appointmentDate = formatDate(appointment?.startDate);

        // Keep the appointment date if it is still available
        const availableDate = availableDates.find(
            ({ startDate }) => formatDate(startDate) === appointmentDate,
        );

        // Use the appointment date if available,
        // otherwise use the first available date
        return availableDate
            ? appointmentDate
            : formatDate(availableDates[0]?.startDate) || null;
    })();

    // Set react hook form default values
    const formValues = appointment
        ? {
              date: startDate,
              time: formatTime(appointment.startDate),
              confirmContact: appointment.confirmContact == 1 ? true : false,
              confirmationType: appointment.confirmationType
                  ? [appointment.confirmationType.toString()]
                  : [],
          }
        : undefined;

    const methods = useForm({
        resolver: yupResolver(schema),
        values: formValues,
    });

    const navigate = useNavigate();

    const onCancel = () => {
        navigate("/patient/appointments");
    };

    const mutation = useUpdateAppointment();

    const onSubmit = (data) => {
        mutation.mutate({
            appointmentGuid: appointment.appointmentGuid,
            data,
            statusId: 0,
        });
    };

    if (isLoading) {
        return (
            <div className="grid h-75 place-items-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (appointment.statusID !== 0 && appointment.statusID !== 1) {
        return <Navigate to="/patient/appointments" replace />;
    }

    return (
        <div className="container mx-auto">
            <AppointmentForm
                mode="edit"
                context={appointment}
                clinicianId={appointment.clinicianID}
                methods={methods}
                onCancel={onCancel}
                onSubmit={onSubmit}
                startDate={startDate}
                availableDates={availableDates}
                onMonthChange={setActiveMonth}
                isPending={mutation.isPending || mutation.isSuccess}
                isLoading={isAvailableDatesLoading}
            />
        </div>
    );
};
